import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SuccessToast from '../../components/ui/SuccessToast';
import SkeletonCard from '../../components/ui/SkeletonCard';

const vehicleLabels = { bike: 'Bike', boda: 'Boda Boda', tuktuk: 'Tuk Tuk', car: 'Car' };
const vehicleIcons = { bike: '🚲', boda: '🏍️', tuktuk: '🛺', car: '🚗' };
const paymentIcons = { mpesa: '📱', cash: '💵', wallet: '👛', card: '💳' };
const paymentLabels = { mpesa: 'M-Pesa', cash: 'Cash', wallet: 'OnTheGo Wallet', card: 'Card' };

const ETATimer = ({ initialMinutes = 5 }) => {
  const [minutes, setMinutes] = useState(initialMinutes);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSeconds(prev => {
        if (prev === 0) {
          setMinutes(m => {
            if (m <= 0) { clearInterval(timerRef?.current); return 0; }
            return m - 1;
          });
          return 59;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef?.current);
  }, []);

  const totalSecs = initialMinutes * 60;
  const remaining = minutes * 60 + seconds;
  const progress = totalSecs > 0 ? ((totalSecs - remaining) / totalSecs) * 100 : 0;
  const urgencyColor = minutes < 2 ? '#ef4444' : minutes < 5 ? '#f59e0b' : '#ffd700';
  const circumference = 2 * Math.PI * 22;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-16 h-16 flex-shrink-0">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
          <circle
            cx="28" cy="28" r="22" fill="none"
            stroke={urgencyColor} strokeWidth="4" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress / 100)}
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill={urgencyColor} className="w-5 h-5">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
          </svg>
        </div>
      </div>
      <div className="flex-1">
        <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-0.5">Driver Arrives In</p>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black tabular-nums" style={{ color: urgencyColor }}>
            {String(minutes)?.padStart(2, '0')}
          </span>
          <span className="text-2xl font-bold text-white/40">:</span>
          <span className="text-4xl font-black tabular-nums" style={{ color: urgencyColor }}>
            {String(seconds)?.padStart(2, '0')}
          </span>
          <span className="text-sm text-white/40 font-medium ml-1">min</span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="flex items-center justify-end gap-1.5 mb-1">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
          <span className="text-xs text-green-400 font-medium">Live</span>
        </div>
        <p className="text-white/40 text-xs">En route</p>
      </div>
    </div>
  );
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 24 24" fill={i < Math.floor(rating) ? '#ffd700' : 'rgba(255,255,255,0.2)'} className="w-3.5 h-3.5">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
    <span className="text-xs text-white/60 ml-1 font-medium">{rating}</span>
  </div>
);

const BookingConfirmationScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams?.get('orderId');

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [driver, setDriver] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [error, setError] = useState(null);
  const [receiptToast, setReceiptToast] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmationToast, setConfirmationToast] = useState(true);

  useEffect(() => {
    if (orderId) {
      fetchOrderData(orderId);
    } else {
      setLoading(false);
      setError('No order ID provided.');
    }
  }, [orderId]);

  const fetchOrderData = async (id) => {
    try {
      setLoading(true);
      const { data: orderData, error: orderErr } = await supabase?.from('orders')?.select('*')?.eq('id', id)?.single();

      if (orderErr || !orderData) {
        setError('Order not found.');
        setLoading(false);
        return;
      }

      setOrder(orderData);

      if (orderData?.driver_id) {
        const { data: driverProfileData } = await supabase?.from('driver_profiles')?.select('*, user_profiles(full_name, avatar_url, phone)')?.eq('id', orderData?.driver_id)?.single();

        if (driverProfileData) {
          setDriverProfile(driverProfileData);
          setDriver({
            name: driverProfileData?.user_profiles?.full_name || 'James Mwangi',
            phone: driverProfileData?.user_profiles?.phone || '+254 712 345 678',
            avatar: driverProfileData?.user_profiles?.avatar_url || null,
            initials: (driverProfileData?.user_profiles?.full_name || 'JM')?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)?.toUpperCase(),
            rating: parseFloat(driverProfileData?.rating) || 4.9,
            trips: driverProfileData?.total_trips || 1234,
            vehicleType: vehicleLabels?.[driverProfileData?.vehicle_type] || 'Boda Boda',
            vehicleIcon: vehicleIcons?.[driverProfileData?.vehicle_type] || '🏍️',
            vehicleName: driverProfileData?.vehicle_name || 'Honda CB300',
            vehicleColor: driverProfileData?.vehicle_color || 'Black',
            licensePlate: driverProfileData?.license_plate || 'KBZ 123A',
          });
        }
      }

      if (!orderData?.driver_id) {
        setDriver({
          name: 'James Mwangi',
          phone: '+254 712 345 678',
          avatar: null,
          initials: 'JM',
          rating: 4.9,
          trips: 1234,
          vehicleType: vehicleLabels?.[orderData?.vehicle_type] || 'Boda Boda',
          vehicleIcon: vehicleIcons?.[orderData?.vehicle_type] || '🏍️',
          vehicleName: 'Honda CB300',
          vehicleColor: 'Black',
          licensePlate: 'KBZ 123A',
        });
      }
    } catch (err) {
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  };

  const baseFare = parseFloat(order?.fare_amount || order?.total_amount || 0);
  const totalAmount = parseFloat(order?.total_amount || 0);
  const platformFee = parseFloat(order?.platform_fee || 0);
  const discount = Math.max(0, baseFare - totalAmount + platformFee);
  const hasDiscount = discount > 0;

  const showToast = (msg) => {
    setReceiptToast(msg);
    setTimeout(() => setReceiptToast(''), 3000);
  };

  const handleDownloadPDF = () => {
    const receiptContent = `
OnTheGo Booking Receipt
========================
Order ID: ${order?.order_number || orderId?.slice(0, 8)?.toUpperCase()}
Date: ${new Date(order?.created_at)?.toLocaleString()}

FROM: ${order?.pickup_address}
TO:   ${order?.dropoff_address}

Vehicle: ${vehicleLabels?.[order?.vehicle_type] || 'Boda Boda'}
Driver: ${driver?.name}
Plate: ${driver?.licensePlate}

FARE BREAKDOWN
--------------
Base Fare:    KSh ${baseFare?.toFixed(0)}
${hasDiscount ? `Promo Discount: -KSh ${discount?.toFixed(0)}\n` : ''}Platform Fee: KSh ${platformFee?.toFixed(0)}
Total Paid:   KSh ${totalAmount?.toFixed(0)}

Payment: ${paymentLabels?.[order?.payment_method] || 'Cash'}
Status: ${order?.payment_status === 'paid' ? 'PAID' : 'PENDING'}

Thank you for riding with OnTheGo!
    `?.trim();

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OTG-Receipt-${order?.order_number || 'booking'}.txt`;
    a?.click();
    URL.revokeObjectURL(url);
    showToast('Receipt downloaded!');
  };

  const handleShare = async () => {
    const shareText = `OnTheGo Booking Confirmed!\nOrder: ${order?.order_number || orderId?.slice(0, 8)?.toUpperCase()}\nFrom: ${order?.pickup_address}\nTo: ${order?.dropoff_address}\nFare: KSh ${totalAmount?.toFixed(0)}`;
    if (navigator?.share) {
      try {
        await navigator.share({ title: 'OnTheGo Booking', text: shareText });
        showToast('Shared successfully!');
      } catch {}
    } else {
      await navigator.clipboard?.writeText(shareText);
      showToast('Booking details copied!');
    }
    setShareOpen(false);
  };

  const handleEmailReceipt = () => {
    const subject = encodeURIComponent(`OnTheGo Receipt - ${order?.order_number || orderId?.slice(0, 8)?.toUpperCase()}`);
    const body = encodeURIComponent(`Your OnTheGo booking receipt:\n\nOrder: ${order?.order_number}\nFrom: ${order?.pickup_address}\nTo: ${order?.dropoff_address}\nTotal: KSh ${totalAmount?.toFixed(0)}\n\nThank you for riding with OnTheGo!`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    showToast('Email client opened!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628]">
        {/* Skeleton header */}
        <div className="px-5 pt-12 pb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-white/10 animate-pulse" />
            <div className="space-y-2">
              <div className="h-3 w-28 rounded-full bg-white/10 animate-pulse" />
              <div className="h-5 w-40 rounded-full bg-white/15 animate-pulse" />
            </div>
          </div>
          {/* ETA skeleton */}
          <div className="bg-white/8 rounded-3xl p-5 border border-white/10 mb-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/10 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 rounded-full bg-white/10 animate-pulse" />
                <div className="h-10 w-28 rounded-xl bg-white/15 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
        {/* Skeleton content */}
        <div className="bg-white rounded-t-[2rem] min-h-screen px-5 pt-6 pb-10 space-y-5">
          {/* Driver card skeleton */}
          <div>
            <div className="h-3 w-20 rounded-full bg-[#0a1628]/10 animate-pulse mb-3" />
            <div className="bg-[#0a1628] rounded-3xl p-5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-white/10 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-32 rounded-full bg-white/15 animate-pulse" />
                  <div className="h-3 w-20 rounded-full bg-white/10 animate-pulse" />
                  <div className="h-3 w-24 rounded-full bg-white/8 animate-pulse" />
                </div>
                <div className="w-20 h-9 rounded-xl bg-[#ffd700]/20 animate-pulse" />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[0,1,2]?.map(i => (
                  <div key={i} className="h-16 rounded-2xl bg-white/8 animate-pulse" />
                ))}
              </div>
              <div className="flex gap-3">
                <div className="flex-1 h-12 rounded-2xl bg-[#ffd700]/20 animate-pulse" />
                <div className="flex-1 h-12 rounded-2xl bg-white/10 animate-pulse" />
              </div>
            </div>
          </div>
          {/* Route skeleton */}
          <SkeletonCard lines={2} className="border-[#0a1628]/8" />
          {/* Fare skeleton */}
          <SkeletonCard lines={4} className="border-[#0a1628]/8" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" className="w-8 h-8">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="text-white font-bold text-xl mb-2">Order Not Found</h2>
          <p className="text-white/50 text-sm mb-6">{error || 'We could not find this booking.'}</p>
          <button
            onClick={() => navigate('/customer-booking-screen')}
            className="px-6 py-3 bg-[#ffd700] text-[#0a1628] font-bold rounded-2xl hover:bg-[#ffd700]/90 transition-colors"
          >
            Book a Ride
          </button>
        </div>
      </div>
    );
  }

  const orderRef = order?.order_number || orderId?.slice(0, 8)?.toUpperCase();
  const etaMinutes = 5;

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Booking confirmed success toast */}
      <SuccessToast
        visible={confirmationToast}
        message="Booking Confirmed! 🎉"
        subMessage={`Order ${order?.order_number || orderId?.slice(0, 8)?.toUpperCase()} · Driver en route`}
        onDismiss={() => setConfirmationToast(false)}
        duration={4000}
        position="top"
      />
      {/* Receipt action toast */}
      {receiptToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white text-sm font-semibold px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2 animate-bounce">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {receiptToast}
        </div>
      )}
      {/* Header */}
      <div className="px-5 pt-12 pb-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/customer-booking-screen')}
            className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-5 h-5">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div>
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest">Booking Confirmed</p>
            <h1 className="text-white font-black text-xl leading-tight">Your ride is on the way</h1>
          </div>
        </div>

        {/* Success badge + Order ID */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="w-4 h-4">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <p className="text-white/40 text-xs">Order ID</p>
              <p className="text-white font-bold font-mono text-sm tracking-wider">{orderRef}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white/40 text-xs">{new Date(order?.created_at)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            <p className="text-white/60 text-xs">{new Date(order?.created_at)?.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>

        {/* ETA Countdown */}
        <div className="bg-white/8 rounded-3xl p-5 border border-white/10 mb-5">
          <ETATimer initialMinutes={etaMinutes} />
        </div>
      </div>
      {/* Scrollable content */}
      <div className="bg-white rounded-t-[2rem] min-h-screen px-5 pt-6 pb-10 space-y-5">

        {/* Driver Card */}
        {driver && (
          <div>
            <p className="text-xs font-bold text-[#0a1628]/40 uppercase tracking-widest mb-3">Your Driver</p>
            <div className="bg-[#0a1628] rounded-3xl p-5">
              <div className="flex items-center gap-4 mb-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {driver?.avatar ? (
                    <img src={driver?.avatar} alt={`${driver?.name} profile photo`} className="w-16 h-16 rounded-2xl object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ffd700] to-[#f59e0b] flex items-center justify-center text-2xl font-black text-[#0a1628]">
                      {driver?.initials}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-2 border-[#0a1628] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-lg leading-tight">{driver?.name}</p>
                  <StarRating rating={driver?.rating} />
                  <p className="text-white/40 text-xs mt-1">{driver?.trips?.toLocaleString()} trips completed</p>
                </div>

                {/* Plate */}
                <div className="text-right flex-shrink-0">
                  <p className="text-white/40 text-xs mb-1">Plate No.</p>
                  <div className="bg-[#ffd700] text-[#0a1628] font-black text-sm px-3 py-1.5 rounded-xl tracking-widest">
                    {driver?.licensePlate}
                  </div>
                </div>
              </div>

              {/* Vehicle info */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: 'Vehicle', value: driver?.vehicleName, icon: driver?.vehicleIcon },
                  { label: 'Type', value: driver?.vehicleType, icon: null },
                  { label: 'Color', value: driver?.vehicleColor, icon: null },
                ]?.map(item => (
                  <div key={item?.label} className="bg-white/8 rounded-2xl p-3 text-center">
                    {item?.icon && <p className="text-xl mb-1">{item?.icon}</p>}
                    <p className="text-white/40 text-xs">{item?.label}</p>
                    <p className="text-white text-xs font-semibold mt-0.5 truncate">{item?.value}</p>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <a
                  href={`tel:${driver?.phone}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#ffd700] rounded-2xl text-[#0a1628] font-bold text-sm hover:bg-[#ffd700]/90 transition-colors active:scale-95"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                  </svg>
                  Call Driver
                </a>
                <button
                  onClick={() => navigate(`/order-tracking-screen?orderId=${orderId}`)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/10 rounded-2xl text-white font-bold text-sm hover:bg-white/20 transition-colors active:scale-95"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Track Ride
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trip Route */}
        <div>
          <p className="text-xs font-bold text-[#0a1628]/40 uppercase tracking-widest mb-3">Trip Route</p>
          <div className="bg-[#0a1628]/4 rounded-3xl p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center gap-1 mt-1 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-[#ffd700] border-2 border-[#0a1628]"></div>
                <div className="w-0.5 h-8 bg-[#0a1628]/20 rounded-full"></div>
                <div className="w-3 h-3 rounded-sm bg-[#0a1628] rotate-45"></div>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-xs text-[#0a1628]/40 font-semibold uppercase tracking-wider">Pickup</p>
                  <p className="text-sm font-semibold text-[#0a1628] mt-0.5">{order?.pickup_address}</p>
                </div>
                <div>
                  <p className="text-xs text-[#0a1628]/40 font-semibold uppercase tracking-wider">Drop-off</p>
                  <p className="text-sm font-semibold text-[#0a1628] mt-0.5">{order?.dropoff_address}</p>
                </div>
              </div>
            </div>
            {order?.distance_km && (
              <div className="mt-3 pt-3 border-t border-[#0a1628]/8 flex items-center gap-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0a1628]/40">
                  <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" />
                </svg>
                <span className="text-xs text-[#0a1628]/50 font-medium">{parseFloat(order?.distance_km)?.toFixed(1)} km total distance</span>
              </div>
            )}
          </div>
        </div>

        {/* Fare Breakdown */}
        <div>
          <p className="text-xs font-bold text-[#0a1628]/40 uppercase tracking-widest mb-3">Fare Breakdown</p>
          <div className="bg-[#0a1628]/4 rounded-3xl p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#0a1628]/60">Base Fare</span>
              <span className="text-sm font-semibold text-[#0a1628]">KSh {baseFare?.toFixed(0)}</span>
            </div>

            {platformFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#0a1628]/60">Platform Fee</span>
                <span className="text-sm font-semibold text-[#0a1628]">KSh {platformFee?.toFixed(0)}</span>
              </div>
            )}

            {hasDiscount && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" className="w-3 h-3">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-sm text-green-600 font-semibold">Promo Discount</span>
                </div>
                <span className="text-sm font-bold text-green-600">- KSh {discount?.toFixed(0)}</span>
              </div>
            )}

            <div className="pt-3 border-t border-[#0a1628]/10">
              <div className="flex justify-between items-center">
                <span className="text-base font-bold text-[#0a1628]">Total Paid</span>
                <span className="text-xl font-black text-[#0a1628]">KSh {totalAmount?.toFixed(0)}</span>
              </div>
            </div>

            {/* Payment method */}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-base">{paymentIcons?.[order?.payment_method] || '💵'}</span>
              <span className="text-xs text-[#0a1628]/50">Paid via <span className="font-semibold text-[#0a1628]">{paymentLabels?.[order?.payment_method] || 'Cash'}</span></span>
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${order?.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {order?.payment_status === 'paid' ? 'Paid' : 'Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Receipt Options */}
        <div>
          <p className="text-xs font-bold text-[#0a1628]/40 uppercase tracking-widest mb-3">Booking Receipt</p>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={handleDownloadPDF}
              className="flex flex-col items-center gap-2 p-4 bg-[#0a1628]/4 rounded-2xl hover:bg-[#0a1628]/8 transition-colors active:scale-95 group"
            >
              <div className="w-10 h-10 rounded-xl bg-[#0a1628] flex items-center justify-center group-hover:bg-[#1a365d] transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="#ffd700" strokeWidth="2" className="w-5 h-5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-[#0a1628] text-center leading-tight">Download&lt;br/&gt;Receipt</span>
            </button>

            <button
              onClick={handleShare}
              className="flex flex-col items-center gap-2 p-4 bg-[#0a1628]/4 rounded-2xl hover:bg-[#0a1628]/8 transition-colors active:scale-95 group"
            >
              <div className="w-10 h-10 rounded-xl bg-[#0a1628] flex items-center justify-center group-hover:bg-[#1a365d] transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="#ffd700" strokeWidth="2" className="w-5 h-5">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" strokeLinecap="round" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-[#0a1628] text-center leading-tight">Share&lt;br/&gt;Booking</span>
            </button>

            <button
              onClick={handleEmailReceipt}
              className="flex flex-col items-center gap-2 p-4 bg-[#0a1628]/4 rounded-2xl hover:bg-[#0a1628]/8 transition-colors active:scale-95 group"
            >
              <div className="w-10 h-10 rounded-xl bg-[#0a1628] flex items-center justify-center group-hover:bg-[#1a365d] transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="#ffd700" strokeWidth="2" className="w-5 h-5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-[#0a1628] text-center leading-tight">Email&lt;br/&gt;Receipt</span>
            </button>
          </div>
        </div>

        {/* Bottom actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => navigate(`/order-tracking-screen?orderId=${orderId}`)}
            className="flex-1 py-4 bg-[#0a1628] rounded-2xl text-[#ffd700] font-bold text-base hover:bg-[#1a365d] active:scale-[0.98] transition-all shadow-lg"
          >
            Track My Ride
          </button>
          <button
            onClick={() => navigate('/customer-booking-screen')}
            className="px-5 py-4 bg-[#0a1628]/8 rounded-2xl text-[#0a1628] font-bold text-sm hover:bg-[#0a1628]/12 active:scale-[0.98] transition-all"
          >
            New Booking
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationScreen;
